/**
 * PF v2 native modules — JSX component map (A-01).
 *
 * The per-`(cartItem, flow)` native UI for each migrated flow. Imported ONLY by
 * the configuration page (`ProductConfiguration.page.tsx`) and its tab confirmation —
 * i.e. the UI layer. The STORE layer must NEVER import this file (it pulls in
 * React components and would cycle back through `store → selectors → completeness`
 * — see `modules/completeness.ts`, the pure other half of the split).
 *
 * Populated as each module migrates. A flow absent here still renders via the
 * legacy `AdapterPanel` (see the incremental switch in `ProductConfiguration.page`)
 * until its module lands. `PF_CONFIG_MODULES` membership is also the discriminator
 * the configuration page uses to branch native-vs-legacy on click (MODULE-CONTRACT §4b).
 */

import type React from 'react'

import type { CartItemId, ConfigurationFlowId } from 'models/purchaseFlowRevamp'
// Event-out type survives the rewrite; the imperative adapter handle does not.
import type { CartMutationHandler } from '../adapters/bridge'

import { measurementModule } from './measurement/measurement.module'
import { floorPlanCertificationModule } from './floorPlanCertification/floorPlanCertification.module'
import { propertyInfoModule } from './propertyInfo/propertyInfo.module'
import { authoritiesDocumentModule } from './authoritiesDocument/authoritiesDocument.module'
import { consumptionCertificateModule } from './consumptionCertificate/consumptionCertificate.module'
import { energyCertificateOfNeedModule } from './energyCertificateOfNeed/energyCertificateOfNeed.module'
import { floorPlanConfigModule } from './floorPlanConfig/floorPlanConfig.module'
import { stagingModule } from './staging/staging.module'
import { editingConfigModule } from './editingConfig/editingConfig.module'

export interface PfConfigModule {
  flowId: ConfigurationFlowId
  /** Native UI bound to one cart item. Reads/writes via the module's ops hook. */
  Component: React.ComponentType<{ cartItemId: CartItemId; onCartMutation?: CartMutationHandler }>
}

export const PF_CONFIG_MODULES: Partial<Record<ConfigurationFlowId, PfConfigModule>> = {
  measurement: measurementModule,
  floor_plan_certification: floorPlanCertificationModule,
  // Not registered as a tab of its own: `property_info` is a PAGE of the
  // document configurator, which `authoritiesDocumentModule` hosts. It keeps its
  // module entry so the flow id stays addressable (store key, completeness).
  property_info: propertyInfoModule,
  power_of_attorney: authoritiesDocumentModule,
  consumption_certificate: consumptionCertificateModule,
  energy_certificate_of_need: energyCertificateOfNeedModule,
  floor_plan_config: floorPlanConfigModule,
  staging: stagingModule,
  // Derived FE-side rather than announced by the catalogue — see
  // `utils/purchaseFlowRevamp/sourceUploadFlow.ts`.
  editing_config: editingConfigModule,
}
